import React, { useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { animated } from '@react-spring/web'
import { ReactPortal } from '~src/new/shared/ui/ReactPortal/ReactPortal'
import cls from './modal.module.scss'
import Icon from '@material-ui/core/Icon'
import { useModalAnimation } from '~src/new/shared/ui/Modal/useModalAnimation'
import { useWidth } from '~src/new/app/providers/WidthProvider'
import { Button } from '@/shared/ui/buttons/Button/Button'
import { BackButtonLine } from '@/shared/ui/BackButtonLine/BackButtonLine'
import { ModalSheet } from '../ModalSheet'
import { usePopup } from '~shared/components/popup/PopupContext'

export const ModalFront = props => {
  const {
    className,
    classNameBody,
    classNameOverlay,
    backButtonClassName,
    children,
    isOpen,
    onClose,
    withBackButton,
    useBackButtonComponent,
    onBackClick,
    contentStyle,
    headerTitle,
    parent,
    footerElement,
    noPortal,
    hideCross,
    showMobileCloseButton,
    disableAnimation,
    disableMobileSheet,
    mobileSheetProps,
    containerProps,
    noGutters,
    dontReturnOverflow,
    contained,
    info,
    footerConfig = {},
  } = props

  const {
    enableFooter = false,
    footerButtonText = 'Подробнее',
    footerHandler,
    customFooter,
  } = footerConfig

  const { isMobile } = useWidth()
  const { popupRef } = usePopup()

  const closeHandler = useCallback(() => {
    if (onClose) {
      onClose()
    }
  }, [onClose])

  const onContentClick = e => {
    e.stopPropagation()
  }

  const onKeyDown = useCallback(
    e => {
      if (e.key === 'Escape') {
        closeHandler()
      }
    },
    [closeHandler],
  )

  useEffect(() => {
    if (!isOpen) return
    const overflow = dontReturnOverflow ? undefined : document.body.style.overflow
    const overscrollBehaviorBody = document.body.style.overscrollBehavior
    const overscrollBehaviorHtml = document.documentElement.style.overscrollBehavior

    window.addEventListener('keydown', onKeyDown)
    document.body.style.overflow = 'hidden'
    document.body.style.overscrollBehavior = 'none'
    document.documentElement.style.overscrollBehavior = 'none'

    return () => {
      window.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = overflow
      document.body.style.overscrollBehavior = overscrollBehaviorBody
      document.documentElement.style.overscrollBehavior = overscrollBehaviorHtml
    }
  }, [dontReturnOverflow, onKeyDown, isOpen])

  const isEnableAnimation = isMobile && disableAnimation !== true && !disableMobileSheet

  const {
    handleSwipe,
    isRenderChildren: isRenderChildrenByAnimation,
    style: animateStyle,
  } = useModalAnimation(isOpen, onClose, !isEnableAnimation)

  if (isOpen && isMobile && !disableMobileSheet) {
    return (
      <ModalSheet
        isOpen={isOpen}
        onClose={onClose}
        snapPoints={[-64 - 20, 0]}
        footerConfig={footerConfig}
        title={headerTitle}
        mountPoint={popupRef?.current}
        showCloseButton={showMobileCloseButton}
        {...(mobileSheetProps || {})}
      >
        {children}
      </ModalSheet>
    )
  }

  const isRenderChildren = isEnableAnimation ? isRenderChildrenByAnimation : isOpen

  const mods = {
    [cls.opened]: isOpen,
    [cls.contained]: contained,
  }

  const withFooter = Boolean(footerElement)

  const ReactPortalNode = noPortal ? React.Fragment : ReactPortal

  return (
    <ReactPortalNode element={parent}>
      <div className={classNames(cls.modal, mods)} {...(containerProps || {})}>
        <div
          className={classNames(
            cls.overlay,
            classNameOverlay,
            disableMobileSheet && cls['no-mobile-sheet-overlay'],
          )}
          onClick={closeHandler}
        >
          <animated.div
            className={classNames(
              cls.content,
              className,
              withFooter && cls.withFooter,
              disableMobileSheet && cls['no-mobile-sheet-content'],
            )}
            style={{
              ...(contentStyle || {}),
              ...(isEnableAnimation ? animateStyle || {} : {}),
            }}
            onClick={onContentClick}
            {...(isEnableAnimation ? handleSwipe || {} : {})}
          >
            {!hideCross && (
              <div className={classNames(cls.close)} onClick={closeHandler}>
                <Icon>close</Icon>
              </div>
            )}

            {withBackButton && !useBackButtonComponent && (
              <div className={classNames(cls.back, backButtonClassName)} onClick={onBackClick}>
                <Icon>arrow_back</Icon>
              </div>
            )}
            {withBackButton && useBackButtonComponent && (
              <BackButtonLine
                className={classNames(cls.backButtonLine, backButtonClassName)}
                onClick={onBackClick}
              />
            )}

            {isRenderChildren ? (
              <div
                className={classNames(cls.body, classNameBody, {
                  [cls['no-gutters']]: noGutters,
                })}
              >
                {children}
              </div>
            ) : (
              <ModalContentBoilerplate headerTitle={headerTitle} info={info} />
            )}
            {(enableFooter || customFooter) && (
              <div className={cls.footer}>
                {customFooter ? (
                  customFooter
                ) : (
                  <Button onClick={footerHandler} className={cls.footerButton}>
                    {footerButtonText}
                  </Button>
                )}
              </div>
            )}
          </animated.div>
        </div>
      </div>
    </ReactPortalNode>
  )
}

const ModalContentBoilerplate = ({ headerTitle, info }) => {
  return (
    <div>
      <div>
        <img src="" alt="" />
      </div>
      <div className={cls.container}>
        {headerTitle && <h3 className={cls.title}>{headerTitle}</h3>}
        {info && <p className={cls.info}>{info}</p>}
      </div>
    </div>
  )
}

ModalFront.propTypes = {
  className: PropTypes.string,
  classNameBody: PropTypes.string,
  classNameOverlay: PropTypes.string,
  backButtonClassName: PropTypes.string,
  popupBarStyle: PropTypes.object,
  children: PropTypes.node,
  parent: PropTypes.node,
  noPortal: PropTypes.bool,
  hideCross: PropTypes.bool,
  showMobileCloseButton: PropTypes.bool,
  noGutters: PropTypes.bool,
  dontReturnOverflow: PropTypes.bool,
  contained: PropTypes.bool,

  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  withBackButton: PropTypes.bool,
  useBackButtonComponent: PropTypes.bool,
  onBackClick: PropTypes.func,

  contentStyle: PropTypes.object,

  headerTitle: PropTypes.node,
  info: PropTypes.node,

  footerElement: PropTypes.node,
  footerConfig: PropTypes.shape({
    enableFooter: PropTypes.bool,
    footerButtonText: PropTypes.node,
    footerHandler: PropTypes.func,
    customFooter: PropTypes.node,
  }),
  containerProps: PropTypes.object,

  disableAnimation: PropTypes.bool,
  disableMobileSheet: PropTypes.bool,
  mobileSheetProps: PropTypes.object,
}

ModalContentBoilerplate.propTypes = {
  headerTitle: PropTypes.node,
  info: PropTypes.node,
}
